<script setup lang="ts">
import type { RouteLocation } from "#vue-router";
import OverviewAuthorsList from "~/components/templates/v2/RTOverview/OverviewAuthorsList.vue";
import RTOverviewBackground from "~/components/templates/v2/RTOverview/RTOverviewBackground.vue";
import ArticleCard from "~/components/templates/v2/ArticleCard.vue";
import { scrollToItem } from "~/helpers";
import { useEnvironmentConfigStore } from "~/stores/environmentConfig";

const mainStore = useMain();
const route = useRoute();
const terminology = useTerminologyStore();
const runtimeConfig = useRuntimeConfig();
const { isTopicCoordinatorVisible, isTopicEditorsVisible } =
  runtimeConfig.public;
const { isImpactMetricsVisible } = useEnvironmentConfigStore().getConfig();
//const isFrontiers = $frontiersClibConfig.getIsFrontiers;

const metaData = usePageMetaInfo().getResearchTopicDetailsPageMetaInfo(
  mainStore.currentResearchTopic
);

useHead(() => metaData);

const viewArticlesLink = computed((): string | RouteLocation => {
  return !route.params.rtTabs
    ? `${route.path}/articles`
    : {
        ...route,
        params: {
          ...route.params,
          rtTabs: "articles"
        }
      };
});

const displayTopicEditors = computed((): boolean => {
  return (
    Boolean(isTopicEditorsVisible) &&
    !!mainStore.currentResearchTopic.topicEditors.length
  );
});

const displayTopicCoordinator = computed((): boolean => {
  return (
    Boolean(isTopicCoordinatorVisible) &&
    !!mainStore.currentResearchTopic.topicCoordinators.length
  );
});

const isImpactViewsVisible = ref<boolean>(isImpactMetricsVisible);

const scrollToMainTabs = () => {
  //This setTimeout is used to wait for the iBar to reset correctly before scrolling
  setTimeout(() => scrollToItem("overview-section-title"), 400);
};
</script>

<template>
  <div
    id="overview-section-title"
    class="RTOverview">
    <RTOverviewBackground />

    <!-- Topic Editors -->
    <OverviewAuthorsList
      v-if="displayTopicEditors"
      topic-title="editors"
      :topic-editors-or-coordinators="
        mainStore.currentResearchTopic.topicEditors
      " />

    <!-- Topic coordinators -->
    <OverviewAuthorsList
      v-if="displayTopicCoordinator"
      topic-title="coordinators"
      :topic-editors-or-coordinators="
        mainStore.currentResearchTopic.topicCoordinators
      " />

    <!-- Articles -->
    <section
      v-if="mainStore.totalArticles"
      class="Articles">
      <div class="Articles__Header">
        <h2 class="Articles__Header__Title h2">
          {{ terminology.getTerm("articles") }}
        </h2>

        <NuxtLink
          :to="viewArticlesLink"
          class="Articles__Header__Link"
          @click="scrollToMainTabs">
          <span>
            See all
            {{ terminology.getTerm("articles").toLowerCase() }}
            ({{ mainStore.totalArticles }})</span
          >
        </NuxtLink>
      </div>
      <div class="Articles__CardList">
        <ArticleCard
          v-for="article in mainStore.articlesForOverview"
          :key="article.id"
          :article="article"
          :show-views="isImpactViewsVisible" />
      </div>
    </section>
  </div>
</template>

<style lang="scss">
:root {
  --descriptionHeight: 120px;
}

.RTOverview {
  --main-text-img-size: 202px;
  width: var(--layout-col-12);

  @media #{$break-laptop} {
    --main-text-img-size: 142px;
  }

  @media #{$break-laptop-sm} {
    width: var(--layout-col-14);
    --main-text-img-size: 124px;
  }

  @media #{$break-md} {
    --main-text-img-size: 110px;
  }

  @media #{$break-sm} {
    --main-text-img-size: 124px;
  }

  @media #{$break-md} {
    width: 100%;
  }

  .Articles {
    &__Header {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      height: fit-content;
      margin-top: var(--spacing-vertical-xl);

      &__Title {
        margin-top: 0;
      }

      &__Link {
        @include f-16;
        font-weight: 700;
        bottom: -10px;
        right: 0;
        padding-left: spacing(2);
        color: var(--maincolor50);
        font-family: "MuseoSans";
        transition: color 300ms $bezier;
        margin-top: 0;

        &:hover {
          color: var(--maincolor70);
          span {
            text-decoration: underline;
            text-decoration-thickness: 1px;
            text-underline-offset: 2px;
          }
        }

        &::after {
          font-family: $fontIcon;
          content: "\e90e";
          font-size: 16px;
          vertical-align: middle;
        }
      }
    }
  }
}
</style>
